import React, { useCallback, useEffect } from "react";
import {
  Box,
  Grid,
  InputAdornment,
  Typography,
  IconButton,
  Modal,
  TextField,
  Button,
} from "@mui/material";
import axios from "../../api";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { AxiosError } from "axios";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import SoftTypography from "components/SoftTypography";
import {
  Edit as EditIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { Building2Icon } from "lucide-react";
import { RootState } from "store";
import { useSelector } from "react-redux";

interface DivisionUser {
  name: string;
  email: string;
  phone?: string;
  password?: string;
}
interface Division {
  division_id: number;
  division_name: string;
  point_of_contact: string;
  branch_code: string;
  user?: DivisionUser;
}
const ViewCustomerDivisions = () => {
  const { fetchError, fetchSuccess } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth.user);
  const [divisions, setDivisions] = React.useState<Division[]>([]);
  const [openModal, setOpenModal] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [selectedDivision, setSelectedDivision] =
    React.useState<Division | null>(null);
  const [divisionPasswordVisibility, setDivisionPasswordVisibility] =
    React.useState<{
      [key: number]: boolean;
    }>({});
  const fetchDivisionsData = useCallback(async () => {
    try {
      const response = await axios.get(`/divisions/customer/${user?.userId}`);
      setDivisions(response.data.divisions || []);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  }, [user?.userId]);
  useEffect(() => {
    fetchDivisionsData();
  }, [fetchDivisionsData]);
  const handleAddDivision = async (values: any, { resetForm }: any) => {
    try {
      await axios.post(`/divisions/customer/${user?.userId}`, values);
      fetchSuccess("Division added successfully");
      resetForm();
      setOpenModal(false);
      fetchDivisionsData();
    } catch (err) {
      if (err instanceof AxiosError && err.response) {
        fetchError(err.response.data.error);
      }
    }
  };

  const handleUpdateDivision = async (values: any) => {
    try {
      await axios.put(`/divisions/${selectedDivision?.division_id}`, values);

      fetchSuccess("Division updated successfully");
      setOpenModal(false);
      setSelectedDivision(null);
      setEditMode(false);

      fetchDivisionsData();
    } catch (err) {
      if (err instanceof AxiosError && err.response) {
        fetchError(err.response.data.error);
      }
    }
  };

  const openEditModal = (division: Division) => {
    setSelectedDivision(division);
    setEditMode(true);
    setOpenModal(true);
  };

  const openAddModal = () => {
    setSelectedDivision(null);
    setEditMode(false);
    setOpenModal(true);
  };

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h5" fontWeight="600">
            Manage Divisions
          </Typography>

          <Button
            variant="contained"
            sx={{
              backgroundColor: "#4aa4a4",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              "&:hover": { backgroundColor: "#3c8f8f" },
            }}
            onClick={openAddModal}
          >
            Add Division
          </Button>
        </Box>
        <Box>
          {divisions.map((d) => (
            <Box
              key={d.division_id}
              sx={{
                background: "white",
                borderRadius: "12px",
                p: 3,
                mb: 2,
                border: "1px solid #e5e7eb",
                boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
              }}
            >
              {/* TOP ROW */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      background: "#e7f1ff",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      color: "#4a86f7",
                      mr: 1.3,
                    }}
                  >
                    <Building2Icon size={18} />
                  </Box>

                  <Typography variant="h6" fontWeight={600}>
                    {d.division_name}
                  </Typography>
                </Box>

                <Typography fontSize={14} fontWeight={700}>
                  {d.branch_code}
                </Typography>
              </Box>

              {/* SECOND ROW */}
              <Grid container alignItems="center">
                <Grid item xs={2.5}>
                  <Typography fontSize={12} color="#6b7280">
                    Email
                  </Typography>
                  <Typography fontSize={14} fontWeight={600}>
                    {d.user?.email || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={2.5}>
                  <Typography fontSize={12} color="#6b7280">
                    User Name
                  </Typography>
                  <Typography fontSize={14} fontWeight={600}>
                    {d.user?.name || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={2.5}>
                  <Typography fontSize={12} color="#6b7280">
                    Phone
                  </Typography>
                  <Typography fontSize={14} fontWeight={600}>
                    {d.user?.phone || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={3}>
                  <Typography fontSize={12} color="#6b7280">
                    Point of Contact
                  </Typography>
                  <Typography fontSize={14} fontWeight={600} noWrap>
                    {d.point_of_contact || "-"}
                  </Typography>
                </Grid>

                <Grid item xs={1} textAlign="right">
                  <IconButton
                    size="small"
                    onClick={() => openEditModal(d)}
                    sx={{ color: "#7a7f8b" }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </Box>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            background: "white",
            width: "50%",
            margin: "100px auto",
            padding: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" mb={2}>
            {editMode ? "Edit Division" : "Add Division"}
          </Typography>

          <Formik
            enableReinitialize
            initialValues={{
              division_name: selectedDivision?.division_name || "",
              point_of_contact: selectedDivision?.point_of_contact || "",
              branch_code: selectedDivision?.branch_code || "",
              user: {
                name: selectedDivision?.user?.name || "",
                email: selectedDivision?.user?.email || "",
                phone: selectedDivision?.user?.phone || "",
                password: "",
              },
            }}
            validationSchema={Yup.object({
              division_name: Yup.string().required("Division Name is required"),
              point_of_contact: Yup.string().required(
                "Point of Contact is required"
              ),
              branch_code: Yup.string().required("Branch Code is required"),
              user: Yup.object({
                name: Yup.string().required("User Name is required"),
                email: Yup.string()
                  .email("Invalid email")
                  .required("Email is required"),
                phone: Yup.string().required("Phone is required"),
                password: editMode
                  ? Yup.string()
                  : Yup.string().required("Password is required"),
              }),
            })}
            onSubmit={editMode ? handleUpdateDivision : handleAddDivision}
          >
            {({ values, errors, touched, handleChange }) => (
              <Form>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Division Name
                      </SoftTypography>
                      <Field
                        name="division_name"
                        as={TextField}
                        variant="outlined"
                        fullWidth
                        error={!!errors.division_name && touched.division_name}
                        helperText={
                          touched.division_name && errors.division_name
                        }
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Point of Contact
                      </SoftTypography>
                      <Field
                        name="point_of_contact"
                        as={TextField}
                        variant="outlined"
                        fullWidth
                        error={
                          !!errors.point_of_contact && touched.point_of_contact
                        }
                        helperText={
                          touched.point_of_contact && errors.point_of_contact
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Branch Code
                      </SoftTypography>
                      <Field
                        name="branch_code"
                        as={TextField}
                        variant="outlined"
                        fullWidth
                        error={!!errors.branch_code && touched.branch_code}
                        helperText={touched.branch_code && errors.branch_code}
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <SoftTypography variant="caption" fontWeight="bold">
                        User Name
                      </SoftTypography>
                      <TextField
                        name="user.name"
                        value={values.user.name}
                        onChange={handleChange}
                        variant="outlined"
                        fullWidth
                        error={!!errors.user?.name && touched.user?.name}
                        helperText={touched.user?.name && errors.user?.name}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <SoftTypography variant="caption" fontWeight="bold">
                        User Email
                      </SoftTypography>
                      <TextField
                        name="user.email"
                        value={values.user.email}
                        onChange={handleChange}
                        variant="outlined"
                        fullWidth
                        error={!!errors.user?.email && touched.user?.email}
                        helperText={touched.user?.email && errors.user?.email}
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <SoftTypography variant="caption" fontWeight="bold">
                        User Phone
                      </SoftTypography>
                      <TextField
                        name="user.phone"
                        value={values.user.phone}
                        onChange={handleChange}
                        variant="outlined"
                        fullWidth
                        error={!!errors.user?.phone && touched.user?.phone}
                        helperText={touched.user?.phone && errors.user?.phone}
                      />
                    </Box>
                  </Grid>
                  {!editMode && (
                    <Grid item xs={12}>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <SoftTypography variant="caption" fontWeight="bold">
                          User Password
                        </SoftTypography>
                        <TextField
                          name="user.password"
                          type={
                            divisionPasswordVisibility[0] ? "text" : "password"
                          }
                          value={values.user.password}
                          onChange={handleChange}
                          variant="outlined"
                          fullWidth
                          error={
                            !!errors.user?.password && touched.user?.password
                          }
                          helperText={
                            touched.user?.password && errors.user?.password
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() =>
                                    setDivisionPasswordVisibility((prev) => ({
                                      ...prev,
                                      0: !prev[0],
                                    }))
                                  }
                                >
                                  {divisionPasswordVisibility[0] ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </Grid>
                  )}
                </Grid>
                <Button type="submit" variant="contained" sx={{ mt: 2 }}>
                  {editMode ? "Update" : "Save"}
                </Button>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
    </>
  );
};

export default ViewCustomerDivisions;
